import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { ALL_COUNTRIES, ALL_TENANT_ROLES } from '@etiquetachef/types';

/**
 * PropertyFilter da lista global de Usuários (issue #257). As
 * `propertyKey`/`operator` batem 1:1 com o contrato de
 * `searchPlatformUsers` (`platform-user-search.ts`), então o mapeamento
 * token → input na página é direto.
 *
 * Ao contrário da lista de Empresas, aqui **nenhuma combinação é proibida**:
 * só `role`/`tenantId` viram query Firestore; o resto é filtrado em memória
 * depois da agregação por pessoa. Por isso não há grupo "standalone" nem
 * tratamento de `unsupported_combo`.
 *
 * O chrome textual (operadores, "Aplicar", "Limpar") vem do `I18nProvider` do
 * Cloudscape em `main.tsx`; aqui só ficam rótulos e valores.
 */

/** Props do PropertyFilter montadas por `useUserFilter`. */
export type UserFilterConfig = {
  filteringProperties: PropertyFilterProps.FilteringProperty[];
  filteringOptions: PropertyFilterProps.FilteringOption[];
  i18nStrings: PropertyFilterProps.I18nStrings;
};

/**
 * Propriedades, opções e rótulos do PropertyFilter da lista global de Usuários.
 *
 * Origem: #257 — commit b3bc5df63, 2026-08-11 17:08 +0200.
 *
 * Contrato: memoizado por `t`. Booleans (`emailVerified`, `phoneVerified`,
 * `disabled`) vão como as strings `'true'`/`'false'` — é o que o schema do
 * token espera. Papéis e países vêm de `ALL_TENANT_ROLES`/`ALL_COUNTRIES`.
 *
 * Usado por: `features/platformUsers/UserList.tsx`.
 *
 * Dicas: chave ou operador novo exige o mesmo no `PlatformUserSearchTokenSchema`
 * (`packages/types/src/platform-user-search.ts`) e no callable
 * `searchPlatformUsers`.
 */
export function useUserFilter(): UserFilterConfig {
  const { t } = useTranslation();

  return useMemo(() => {
    // Rótulo do grupo de valores sugeridos de uma propriedade.
    const valuesLabel = (label: string): string =>
      t('adminPlatformUsers.filters.valuesLabel', { property: label });

    // Uma propriedade filtrável com seus operadores.
    const prop = (
      key: string,
      label: string,
      operators: NonNullable<PropertyFilterProps.FilteringProperty['operators']>,
    ): PropertyFilterProps.FilteringProperty => ({
      key,
      propertyLabel: label,
      groupValuesLabel: valuesLabel(label),
      operators,
    });

    const filteringProperties: PropertyFilterProps.FilteringProperty[] = [
      prop('displayName', t('adminPlatformUsers.columns.name'), ['=', ':']),
      prop('email', t('adminPlatformUsers.columns.email'), ['=', ':']),
      prop('phone', t('adminPlatformUsers.columns.phone'), ['=', ':']),
      prop('role', t('adminPlatformUsers.columns.role'), ['=']),
      prop('tenantId', t('adminPlatformUsers.filters.tenantId'), ['=']),
      prop('country', t('adminPlatformUsers.filters.country'), ['=']),
      prop('emailVerified', t('adminPlatformUsers.filters.emailVerified'), ['=']),
      prop('phoneVerified', t('adminPlatformUsers.filters.phoneVerified'), ['=']),
      prop('disabled', t('adminPlatformUsers.filters.disabled'), ['=']),
    ];

    const boolOptions = (propertyKey: string): PropertyFilterProps.FilteringOption[] => [
      { propertyKey, value: 'true', label: t('adminPlatformUsers.filters.yes') },
      { propertyKey, value: 'false', label: t('adminPlatformUsers.filters.no') },
    ];

    const filteringOptions: PropertyFilterProps.FilteringOption[] = [
      ...ALL_TENANT_ROLES.map((r) => ({
        propertyKey: 'role',
        value: r,
        label: t(`adminMembers.roles.${r}`),
      })),
      ...ALL_COUNTRIES.map((c) => ({ propertyKey: 'country', value: c, label: c })),
      ...boolOptions('emailVerified'),
      ...boolOptions('phoneVerified'),
      ...boolOptions('disabled'),
    ];

    const i18nStrings: PropertyFilterProps.I18nStrings = {
      filteringPlaceholder: t('adminPlatformUsers.filters.filterPlaceholder'),
      filteringAriaLabel: t('adminPlatformUsers.filters.filterPlaceholder'),
      enteredTextLabel: (value: string): string =>
        t('adminPlatformUsers.filters.useValue', { value }),
    };

    return { filteringProperties, filteringOptions, i18nStrings };
  }, [t]);
}
